import React, { useCallback, useMemo } from 'react';

import moment from 'moment';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { DatePicker, Form } from '@shared/components';
import { pageUI, timeFormat, validPickerFormats } from '@shared/constants';
import { isMobileSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';

import { useAllDayCheckbox } from '../../hooks';
import {
  getDefaultDate,
  getStartDateRule,
  makeEndDateDisabler,
} from '../../utils';
import ActivityTimeInput from '../ActivityTimeInput';
import AllDayCheckbox from '../AllDayCheckbox';

const FormItem = Form.Item;

const ActivityFormDate = ({
  activityTypeName,
  isDisabled,
  disableFutureDays,
  preselectedStartDate,
  preselectedEndDate,
  showAllDayCheckbox,
  showStartDate,
  showEndDate,
  showStartTime,
  showEndTime,
  form,
}) => {
  const { getFieldDecorator, getFieldsValue, getFieldValue, setFieldsValue } =
    form;
  const formValues = getFieldsValue();
  const startDate = formValues.startDate;
  const isMobile = useSelector(isMobileSel);

  const allDay = useAllDayCheckbox({
    form,
    start: startDate,
    end: moment(formValues.endTime, timeFormat),
    page: pageUI.ACTIVITY,
  });

  const shouldDisableEndDate = useCallback(
    endDate => makeEndDateDisabler(startDate)(endDate, disableFutureDays),
    [startDate, disableFutureDays],
  );
  const handleChangeStartDate = useCallback(
    formStartDate => {
      if (showEndDate) {
        const formEndDate = getFieldValue('endDate');
        const endDate = formEndDate && formEndDate.clone();
        if (formStartDate && !endDate) {
          const startDate = formStartDate.clone();
          setFieldsValue({ endDate: startDate });
        } else if (formStartDate && endDate) {
          const startDate = formStartDate.clone();
          const isEndBeforeStart = endDate.isBefore(startDate, 'day');
          setFieldsValue({ endDate: isEndBeforeStart ? startDate : endDate });
        }
      }
    },
    [getFieldValue, setFieldsValue, showEndDate],
  );

  const disabledTimePicker = isDisabled || allDay;
  const timePickerRules = disabledTimePicker
    ? [
        {
          required: false,
        },
      ]
    : [
        {
          required: true,
          message: intl.formatMessage({
            id: 'shared.form.error.isRequired',
          }),
        },
        {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          validator: async (rule, _value) => {
            // Only apply for startTime
            if (rule.field !== 'endTime') return;

            /** @type {import('moment').Moment} */
            const startDate = form.getFieldValue('startDate');
            /** @type {import('moment').Moment} */
            const endDate = form.getFieldValue('endDate');

            if (!startDate || !endDate) return;
            if (endDate.isAfter(startDate)) return;

            // this means endDate is the same as startDate
            const startTime = moment(
              form.getFieldValue('startTime'),
              timeFormat,
            );
            const endTime = moment(form.getFieldValue('endTime'), timeFormat);

            if (!endTime.isAfter(startTime))
              throw new Error('End time should be after start time');
          },
        },
      ];
  const disableMobileDates = useMemo(() => {
    if (isMobile) {
      return {
        minDate: startDate,
        maxDate: disableFutureDays ? 'today' : null,
      };
    }
  }, [isMobile, startDate, disableFutureDays]);
  return (
    <React.Fragment>
      {showStartDate && (
        <FormItem required>
          {getFieldDecorator('startDate', {
            rules: [
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
              ...getStartDateRule(disableFutureDays),
            ],
            validateStatus: false,
            initialValue: getDefaultDate(preselectedStartDate),
          })(
            <DatePicker
              size="large"
              placeholder={intl.formatMessage(
                {
                  id: 'progress.common.ActivityFormDate.field.startDate',
                },
                {
                  type: activityTypeName,
                },
              )}
              onChange={handleChangeStartDate}
              format={validPickerFormats}
              allowClear={false}
              fluid
              floatingLabel
              rounded
              disabled={isDisabled}
              disableDates={disableFutureDays ? 'future' : undefined}
            />,
          )}
        </FormItem>
      )}
      {showEndDate && (
        <FormItem required>
          {getFieldDecorator('endDate', {
            rules: [
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ],
            validateStatus: false,
            initialValue: getDefaultDate(preselectedEndDate),
          })(
            <DatePicker
              size="large"
              placeholder={intl.formatMessage(
                {
                  id: 'progress.common.ActivityFormDate.field.endDate',
                },
                {
                  type: activityTypeName,
                },
              )}
              onChange={handleChangeStartDate}
              format={validPickerFormats}
              allowClear={false}
              fluid
              floatingLabel
              rounded
              disabled={isDisabled}
              disableDates={disableMobileDates}
              disabledDate={isMobile ? undefined : shouldDisableEndDate}
            />,
          )}
        </FormItem>
      )}
      {showAllDayCheckbox && (
        <FormItem>
          <AllDayCheckbox disabled={isDisabled} form={form} />
        </FormItem>
      )}
      {showStartTime && (
        <ActivityTimeInput
          allDay={allDay}
          fieldName="startTime"
          fieldTranslationKey="activityStartTime"
          initialValue={preselectedStartDate}
          rules={timePickerRules}
          disabled={disabledTimePicker}
          form={form}
        />
      )}
      {showEndTime && (
        <ActivityTimeInput
          allDay={allDay}
          fieldName="endTime"
          fieldTranslationKey="activityEndTime"
          initialValue={preselectedStartDate}
          rules={timePickerRules}
          disabled={disabledTimePicker}
          form={form}
        />
      )}
    </React.Fragment>
  );
};

ActivityFormDate.propTypes = {
  form: PropTypes.object.isRequired,
  preselectedStartDate: PropTypes.object,
  preselectedEndDate: PropTypes.object,
  showAllDayCheckbox: PropTypes.bool,
  showStartDate: PropTypes.bool,
  showEndDate: PropTypes.bool,
  showStartTime: PropTypes.bool,
  showEndTime: PropTypes.bool,
  isDisabled: PropTypes.bool.isRequired,
  disableFutureDays: PropTypes.bool,
  activityTypeName: PropTypes.string.isRequired,
};

ActivityFormDate.defaultProps = {
  showAllDayCheckbox: true,
  showStartDate: true,
  showEndDate: true,
  showStartTime: true,
  showEndTime: true,
};

export default ActivityFormDate;
